import React, { useState, useEffect, useRef, useCallback } from "react";
import "./TimerCuberos.css";
import logoGif from "../images/logo.gif";
import starPng from "../images/star.png";

const CUBE_TYPES = [
  { value: 2, label: "2x2x2" },
  { value: 3, label: "Rubik's" },
  { value: 4, label: "4x4x4" },
  { value: 5, label: "5x5x5" },
  { value: 6, label: "6x6x6" },
  { value: 7, label: "7x7x7" },
];

const SCRAMBLE_LENGTHS = [10, 25, 40, 60, 80, 100];

// Colores del cubo (orden: U, D, F, B, R, L)
const CUBE_COLORS = {
  U: '#FFFFFF', // Blanco - Up
  D: '#FFFF00', // Amarillo - Down
  F: '#00FF00', // Verde - Front
  B: '#0000FF', // Azul - Back
  R: '#FF0000', // Rojo - Right
  L: '#FF8000', // Naranja - Left
};

// Inicializar estado del cubo
const createInitialCubeState = (size) => {
  const faces = {
    U: Array(size * size).fill('U'),
    D: Array(size * size).fill('D'),
    F: Array(size * size).fill('F'),
    B: Array(size * size).fill('B'),
    R: Array(size * size).fill('R'),
    L: Array(size * size).fill('L'),
  };
  return faces;
};

// Aplicar movimiento al cubo
const applyMove = (faces, move, size) => {
  const newFaces = { ...faces };
  const s = size;
  
  const rotateFace = (face, clockwise) => {
    const newFace = [...face];
    for (let i = 0; i < s; i++) {
      for (let j = 0; j < s; j++) {
        if (clockwise) {
          newFace[i * s + j] = face[(s - 1 - j) * s + i];
        } else {
          newFace[i * s + j] = face[j * s + (s - 1 - i)];
        }
      }
    }
    return newFace;
  };

  const getRow = (face, row) => face.slice(row * s, row * s + s);
  const getCol = (face, col) => Array.from({ length: s }, (_, i) => face[i * s + col]);
  const setRow = (face, row, values) => { const f = [...face]; values.forEach((v, i) => f[row * s + i] = v); return f; };
  const setCol = (face, col, values) => { const f = [...face]; values.forEach((v, i) => f[i * s + col] = v); return f; };

  const baseMove = move.replace("'", "").replace("2", "");
  const isPrime = move.includes("'");
  const isDouble = move.includes("2");
  const times = isDouble ? 2 : 1;
  
  // Determinar si es un movimiento wide (Rw, Lw, etc.) o middle (M, E, S)
  const isWideMove = baseMove.endsWith("w");
  const isMiddleMove = ["M", "E", "S"].includes(baseMove);
  const coreMove = isWideMove ? baseMove.slice(0, -1) : baseMove;

  for (let t = 0; t < times; t++) {
    switch (coreMove) {
      case 'R':
        newFaces.R = rotateFace(newFaces.R, !isPrime);
        if (!isPrime) {
          const temp = getCol(newFaces.F, s - 1);
          newFaces.F = setCol(newFaces.F, s - 1, getCol(newFaces.U, s - 1));
          newFaces.U = setCol(newFaces.U, s - 1, getCol(newFaces.B, 0).reverse());
          newFaces.B = setCol(newFaces.B, 0, getCol(newFaces.D, s - 1).reverse());
          newFaces.D = setCol(newFaces.D, s - 1, temp);
        } else {
          const temp = getCol(newFaces.F, s - 1);
          newFaces.F = setCol(newFaces.F, s - 1, getCol(newFaces.D, s - 1));
          newFaces.D = setCol(newFaces.D, s - 1, getCol(newFaces.B, 0).reverse());
          newFaces.B = setCol(newFaces.B, 0, getCol(newFaces.U, s - 1).reverse());
          newFaces.U = setCol(newFaces.U, s - 1, temp);
        }
        // Para movimientos wide, también mover rodillos internos
        if (isWideMove && s > 2) {
          // Mover el rodillo interno (entre R y el centro)
          for (let layer = 1; layer < s - 1; layer++) {
            if (!isPrime) {
              const tempInner = getCol(newFaces.F, s - 1 - layer);
              newFaces.F = setCol(newFaces.F, s - 1 - layer, getCol(newFaces.U, s - 1 - layer));
              newFaces.U = setCol(newFaces.U, s - 1 - layer, getCol(newFaces.B, layer).reverse());
              newFaces.B = setCol(newFaces.B, layer, getCol(newFaces.D, s - 1 - layer).reverse());
              newFaces.D = setCol(newFaces.D, s - 1 - layer, tempInner);
            } else {
              const tempInner = getCol(newFaces.F, s - 1 - layer);
              newFaces.F = setCol(newFaces.F, s - 1 - layer, getCol(newFaces.D, s - 1 - layer));
              newFaces.D = setCol(newFaces.D, s - 1 - layer, getCol(newFaces.B, layer).reverse());
              newFaces.B = setCol(newFaces.B, layer, getCol(newFaces.U, s - 1 - layer).reverse());
              newFaces.U = setCol(newFaces.U, s - 1 - layer, tempInner);
            }
          }
        }
        break;
      case 'L':
        newFaces.L = rotateFace(newFaces.L, !isPrime);
        if (!isPrime) {
          const temp = getCol(newFaces.F, 0);
          newFaces.F = setCol(newFaces.F, 0, getCol(newFaces.D, 0));
          newFaces.D = setCol(newFaces.D, 0, getCol(newFaces.B, s - 1).reverse());
          newFaces.B = setCol(newFaces.B, s - 1, getCol(newFaces.U, 0).reverse());
          newFaces.U = setCol(newFaces.U, 0, temp);
        } else {
          const temp = getCol(newFaces.F, 0);
          newFaces.F = setCol(newFaces.F, 0, getCol(newFaces.U, 0));
          newFaces.U = setCol(newFaces.U, 0, getCol(newFaces.B, s - 1).reverse());
          newFaces.B = setCol(newFaces.B, s - 1, getCol(newFaces.D, 0).reverse());
          newFaces.D = setCol(newFaces.D, 0, temp);
        }
        if (isWideMove && s > 2) {
          for (let layer = 1; layer < s - 1; layer++) {
            if (!isPrime) {
              const tempInner = getCol(newFaces.F, layer);
              newFaces.F = setCol(newFaces.F, layer, getCol(newFaces.D, layer));
              newFaces.D = setCol(newFaces.D, layer, getCol(newFaces.B, s - 1 - layer).reverse());
              newFaces.B = setCol(newFaces.B, s - 1 - layer, getCol(newFaces.U, layer).reverse());
              newFaces.U = setCol(newFaces.U, layer, tempInner);
            } else {
              const tempInner = getCol(newFaces.F, layer);
              newFaces.F = setCol(newFaces.F, layer, getCol(newFaces.U, layer));
              newFaces.U = setCol(newFaces.U, layer, getCol(newFaces.B, s - 1 - layer).reverse());
              newFaces.B = setCol(newFaces.B, s - 1 - layer, getCol(newFaces.D, layer).reverse());
              newFaces.D = setCol(newFaces.D, layer, tempInner);
            }
          }
        }
        break;
      case 'U':
        newFaces.U = rotateFace(newFaces.U, !isPrime);
        if (!isPrime) {
          const temp = getRow(newFaces.F, 0);
          newFaces.F = setRow(newFaces.F, 0, getRow(newFaces.R, 0));
          newFaces.R = setRow(newFaces.R, 0, getRow(newFaces.B, 0));
          newFaces.B = setRow(newFaces.B, 0, getRow(newFaces.L, 0));
          newFaces.L = setRow(newFaces.L, 0, temp);
        } else {
          const temp = getRow(newFaces.F, 0);
          newFaces.F = setRow(newFaces.F, 0, getRow(newFaces.L, 0));
          newFaces.L = setRow(newFaces.L, 0, getRow(newFaces.B, 0));
          newFaces.B = setRow(newFaces.B, 0, getRow(newFaces.R, 0));
          newFaces.R = setRow(newFaces.R, 0, temp);
        }
        if (isWideMove && s > 2) {
          for (let layer = 1; layer < s - 1; layer++) {
            if (!isPrime) {
              const tempInner = getRow(newFaces.F, layer);
              newFaces.F = setRow(newFaces.F, layer, getRow(newFaces.R, layer));
              newFaces.R = setRow(newFaces.R, layer, getRow(newFaces.B, layer));
              newFaces.B = setRow(newFaces.B, layer, getRow(newFaces.L, layer));
              newFaces.L = setRow(newFaces.L, layer, tempInner);
            } else {
              const tempInner = getRow(newFaces.F, layer);
              newFaces.F = setRow(newFaces.F, layer, getRow(newFaces.L, layer));
              newFaces.L = setRow(newFaces.L, layer, getRow(newFaces.B, layer));
              newFaces.B = setRow(newFaces.B, layer, getRow(newFaces.R, layer));
              newFaces.R = setRow(newFaces.R, layer, tempInner);
            }
          }
        }
        break;
      case 'D':
        newFaces.D = rotateFace(newFaces.D, !isPrime);
        if (!isPrime) {
          const temp = getRow(newFaces.F, s - 1);
          newFaces.F = setRow(newFaces.F, s - 1, getRow(newFaces.L, s - 1));
          newFaces.L = setRow(newFaces.L, s - 1, getRow(newFaces.B, s - 1));
          newFaces.B = setRow(newFaces.B, s - 1, getRow(newFaces.R, s - 1));
          newFaces.R = setRow(newFaces.R, s - 1, temp);
        } else {
          const temp = getRow(newFaces.F, s - 1);
          newFaces.F = setRow(newFaces.F, s - 1, getRow(newFaces.R, s - 1));
          newFaces.R = setRow(newFaces.R, s - 1, getRow(newFaces.B, s - 1));
          newFaces.B = setRow(newFaces.B, s - 1, getRow(newFaces.L, s - 1));
          newFaces.L = setRow(newFaces.L, s - 1, temp);
        }
        if (isWideMove && s > 2) {
          for (let layer = 1; layer < s - 1; layer++) {
            if (!isPrime) {
              const tempInner = getRow(newFaces.F, s - 1 - layer);
              newFaces.F = setRow(newFaces.F, s - 1 - layer, getRow(newFaces.L, s - 1 - layer));
              newFaces.L = setRow(newFaces.L, s - 1 - layer, getRow(newFaces.B, s - 1 - layer));
              newFaces.B = setRow(newFaces.B, s - 1 - layer, getRow(newFaces.R, s - 1 - layer));
              newFaces.R = setRow(newFaces.R, s - 1 - layer, tempInner);
            } else {
              const tempInner = getRow(newFaces.F, s - 1 - layer);
              newFaces.F = setRow(newFaces.F, s - 1 - layer, getRow(newFaces.R, s - 1 - layer));
              newFaces.R = setRow(newFaces.R, s - 1 - layer, getRow(newFaces.B, s - 1 - layer));
              newFaces.B = setRow(newFaces.B, s - 1 - layer, getRow(newFaces.L, s - 1 - layer));
              newFaces.L = setRow(newFaces.L, s - 1 - layer, tempInner);
            }
          }
        }
        break;
      case 'F':
        newFaces.F = rotateFace(newFaces.F, !isPrime);
        if (!isPrime) {
          const temp = getRow(newFaces.U, s - 1);
          newFaces.U = setRow(newFaces.U, s - 1, getCol(newFaces.L, s - 1).reverse());
          newFaces.L = setCol(newFaces.L, s - 1, getRow(newFaces.D, 0));
          newFaces.D = setRow(newFaces.D, 0, getCol(newFaces.R, 0).reverse());
          newFaces.R = setCol(newFaces.R, 0, temp);
        } else {
          const temp = getRow(newFaces.U, s - 1);
          newFaces.U = setRow(newFaces.U, s - 1, getCol(newFaces.R, 0));
          newFaces.R = setCol(newFaces.R, 0, getRow(newFaces.D, 0).reverse());
          newFaces.D = setRow(newFaces.D, 0, getCol(newFaces.L, s - 1).reverse());
          newFaces.L = setCol(newFaces.L, s - 1, temp);
        }
        if (isWideMove && s > 2) {
          for (let layer = 1; layer < s - 1; layer++) {
            if (!isPrime) {
              const tempInner = getRow(newFaces.U, s - 1 - layer);
              newFaces.U = setRow(newFaces.U, s - 1 - layer, getCol(newFaces.L, s - 1 - layer).reverse());
              newFaces.L = setCol(newFaces.L, s - 1 - layer, getRow(newFaces.D, layer));
              newFaces.D = setRow(newFaces.D, layer, getCol(newFaces.R, layer).reverse());
              newFaces.R = setCol(newFaces.R, layer, tempInner);
            } else {
              const tempInner = getRow(newFaces.U, s - 1 - layer);
              newFaces.U = setRow(newFaces.U, s - 1 - layer, getCol(newFaces.R, layer));
              newFaces.R = setCol(newFaces.R, layer, getRow(newFaces.D, layer).reverse());
              newFaces.D = setRow(newFaces.D, layer, getCol(newFaces.L, s - 1 - layer).reverse());
              newFaces.L = setCol(newFaces.L, s - 1 - layer, tempInner);
            }
          }
        }
        break;
      case 'B':
        newFaces.B = rotateFace(newFaces.B, !isPrime);
        if (!isPrime) {
          const temp = getRow(newFaces.U, 0);
          newFaces.U = setRow(newFaces.U, 0, getCol(newFaces.R, s - 1));
          newFaces.R = setCol(newFaces.R, s - 1, getRow(newFaces.D, s - 1).reverse());
          newFaces.D = setRow(newFaces.D, s - 1, getCol(newFaces.L, 0).reverse());
          newFaces.L = setCol(newFaces.L, 0, temp);
        } else {
          const temp = getRow(newFaces.U, 0);
          newFaces.U = setRow(newFaces.U, 0, getCol(newFaces.L, 0).reverse());
          newFaces.L = setCol(newFaces.L, 0, getRow(newFaces.D, s - 1));
          newFaces.D = setRow(newFaces.D, s - 1, getCol(newFaces.R, s - 1).reverse());
          newFaces.R = setCol(newFaces.R, s - 1, temp);
        }
        if (isWideMove && s > 2) {
          for (let layer = 1; layer < s - 1; layer++) {
            if (!isPrime) {
              const tempInner = getRow(newFaces.U, layer);
              newFaces.U = setRow(newFaces.U, layer, getCol(newFaces.R, s - 1 - layer));
              newFaces.R = setCol(newFaces.R, s - 1 - layer, getRow(newFaces.D, s - 1 - layer).reverse());
              newFaces.D = setRow(newFaces.D, s - 1 - layer, getCol(newFaces.L, layer).reverse());
              newFaces.L = setCol(newFaces.L, layer, tempInner);
            } else {
              const tempInner = getRow(newFaces.U, layer);
              newFaces.U = setRow(newFaces.U, layer, getCol(newFaces.L, layer).reverse());
              newFaces.L = setCol(newFaces.L, layer, getRow(newFaces.D, s - 1 - layer));
              newFaces.D = setRow(newFaces.D, s - 1 - layer, getCol(newFaces.R, s - 1 - layer).reverse());
              newFaces.R = setCol(newFaces.R, s - 1 - layer, tempInner);
            }
          }
        }
        break;
      // Movimientos middle (M, E, S)
      case 'M': // Middle - paralelo a L
        if (!isPrime) {
          const tempM = getCol(newFaces.F, 1);
          newFaces.F = setCol(newFaces.F, 1, getCol(newFaces.U, 1));
          newFaces.U = setCol(newFaces.U, 1, getCol(newFaces.B, s - 2).reverse());
          newFaces.B = setCol(newFaces.B, s - 2, getCol(newFaces.D, 1).reverse());
          newFaces.D = setCol(newFaces.D, 1, tempM);
        } else {
          const tempM = getCol(newFaces.F, 1);
          newFaces.F = setCol(newFaces.F, 1, getCol(newFaces.D, 1));
          newFaces.D = setCol(newFaces.D, 1, getCol(newFaces.B, s - 2).reverse());
          newFaces.B = setCol(newFaces.B, s - 2, getCol(newFaces.U, 1).reverse());
          newFaces.U = setCol(newFaces.U, 1, tempM);
        }
        break;
      case 'E': // Equator - paralelo a D
        if (!isPrime) {
          const tempE = getRow(newFaces.F, Math.floor(s / 2));
          newFaces.F = setRow(newFaces.F, Math.floor(s / 2), getRow(newFaces.L, Math.floor(s / 2)));
          newFaces.L = setRow(newFaces.L, Math.floor(s / 2), getRow(newFaces.B, Math.floor(s / 2)));
          newFaces.B = setRow(newFaces.B, Math.floor(s / 2), getRow(newFaces.R, Math.floor(s / 2)));
          newFaces.R = setRow(newFaces.R, Math.floor(s / 2), tempE);
        } else {
          const tempE = getRow(newFaces.F, Math.floor(s / 2));
          newFaces.F = setRow(newFaces.F, Math.floor(s / 2), getRow(newFaces.R, Math.floor(s / 2)));
          newFaces.R = setRow(newFaces.R, Math.floor(s / 2), getRow(newFaces.B, Math.floor(s / 2)));
          newFaces.B = setRow(newFaces.B, Math.floor(s / 2), getRow(newFaces.L, Math.floor(s / 2)));
          newFaces.L = setRow(newFaces.L, Math.floor(s / 2), tempE);
        }
        break;
      case 'S': // Standing - paralelo a F
        if (!isPrime) {
          const tempS = getRow(newFaces.U, Math.floor(s / 2));
          newFaces.U = setRow(newFaces.U, Math.floor(s / 2), getCol(newFaces.L, Math.floor(s / 2)).reverse());
          newFaces.L = setCol(newFaces.L, Math.floor(s / 2), getRow(newFaces.D, Math.floor(s / 2)));
          newFaces.D = setRow(newFaces.D, Math.floor(s / 2), getCol(newFaces.R, Math.floor(s / 2)).reverse());
          newFaces.R = setCol(newFaces.R, Math.floor(s / 2), tempS);
        } else {
          const tempS = getRow(newFaces.U, Math.floor(s / 2));
          newFaces.U = setRow(newFaces.U, Math.floor(s / 2), getCol(newFaces.R, Math.floor(s / 2)));
          newFaces.R = setCol(newFaces.R, Math.floor(s / 2), getRow(newFaces.D, Math.floor(s / 2)).reverse());
          newFaces.D = setRow(newFaces.D, Math.floor(s / 2), getCol(newFaces.L, Math.floor(s / 2)).reverse());
          newFaces.L = setCol(newFaces.L, Math.floor(s / 2), tempS);
        }
        break;
    }
  }
  
  return newFaces;
};

// Parsear scramble y aplicar movimientos
const parseScrambleToCube = (scrambleStr, cubeSize) => {
  let faces = createInitialCubeState(cubeSize);
  const moves = scrambleStr.split(' ').filter(m => m);
  
  moves.forEach(move => {
    faces = applyMove(faces, move, cubeSize);
  });
  
  return faces;
};

// Componente para mostrar una cara del cubo
const CubeFace = ({ faceData, size, faceName }) => {
  const cellSize = Math.max(20, 40 - size * 3);
  
  return (
    <div 
      className="inline-grid gap-0"
      style={{ 
        gridTemplateColumns: `repeat(${size}, ${cellSize}px)`,
      }}
    >
      {faceData.map((color, i) => (
        <div
          key={i}
          style={{
            width: `${cellSize}px`,
            height: `${cellSize}px`,
            backgroundColor: CUBE_COLORS[color],
            border: '1px solid #333',
          }}
        />
      ))}
    </div>
  );
};

// Componente de visualización 3D del cubo
const CubeVisualization = ({ scramble, cubeSize }) => {
  const [cubeState, setCubeState] = useState(null);
  
  useEffect(() => {
    if (scramble) {
      const state = parseScrambleToCube(scramble, cubeSize);
      setCubeState(state);
    }
  }, [scramble, cubeSize]);
  
  if (!cubeState) return null;
  
  const cellSize = Math.max(15, 35 - cubeSize * 3);
  
  return (
    <div className="flex flex-col items-center gap-2 bg-cyan-50 rounded-lg p-4">
      <p className="text-sm font-semibold text-cyan-700">Vista del Cubo</p>
      <div className="flex flex-wrap justify-center gap-4">
        {/* Cara Superior (U) */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-gray-500 mb-1">U</span>
          <CubeFace faceData={cubeState.U} size={cubeSize} faceName="U" />
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-4">
        {/* Cara Izquierda (L) */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-gray-500 mb-1">L</span>
          <CubeFace faceData={cubeState.L} size={cubeSize} faceName="L" />
        </div>
        {/* Cara Frontal (F) */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-gray-500 mb-1">F</span>
          <CubeFace faceData={cubeState.F} size={cubeSize} faceName="F" />
        </div>
        {/* Cara Derecha (R) */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-gray-500 mb-1">R</span>
          <CubeFace faceData={cubeState.R} size={cubeSize} faceName="R" />
        </div>
        {/* Cara Trasera (B) */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-gray-500 mb-1">B</span>
          <CubeFace faceData={cubeState.B} size={cubeSize} faceName="B" />
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-4">
        {/* Cara Inferior (D) */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-gray-500 mb-1">D</span>
          <CubeFace faceData={cubeState.D} size={cubeSize} faceName="D" />
        </div>
      </div>
    </div>
  );
};

export default function TimerCuberos() {
  const [cubeType, setCubeType] = useState(3);
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isGreenLight, setIsGreenLight] = useState(false); // Luz verde después de 1 seg
  const [scramble, setScramble] = useState("");
  const [times, setTimes] = useState([]); // [{ time: number, dnf: boolean }]
  const [scrambles, setScrambles] = useState([]);
  const [dnfStates, setDnfStates] = useState([]); // Track DNF status for each time
  const [displayMode, setDisplayMode] = useState("all"); // '5', '12', 'all'
  const [isAscending, setIsAscending] = useState(true);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);
  const spacePressStartRef = useRef(0);
  const inspectionTimerRef = useRef(null);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [showCubePreview, setShowCubePreview] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true); // Modo oscuro por defecto

  // Cargar modo oscuro desde localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('timerCuberosDarkMode');
    if (savedDarkMode === 'true') {
      setIsDarkMode(true);
    }
  }, []);

  // Guardar modo oscuro en localStorage
  useEffect(() => {
    localStorage.setItem('timerCuberosDarkMode', isDarkMode.toString());
  }, [isDarkMode]);

  // Generar scramble simple (puede mejorarse con lógica real)
  const generateScramble = useCallback(() => {
    const moves = ["R", "L", "U", "D", "F", "B"];
    const modifiers = ["", "'", "2"];
    let scramble = [];
    let lastMove = "";
    
    // Para cubos de 4x4x4 y superiores, agregar movimientos de rodillos
    const isBigCube = cubeType >= 4;
    const wideMoves = isBigCube ? ["Rw", "Lw", "Uw", "Dw", "Fw", "Bw"] : [];
    const middleMoves = isBigCube ? ["M", "E", "S"] : [];
    const allMoves = [...moves, ...wideMoves, ...middleMoves];
    
    for (let i = 0; i < SCRAMBLE_LENGTHS[CUBE_TYPES.findIndex(c => c.value === cubeType)]; i++) {
      let move;
      do {
        move = allMoves[Math.floor(Math.random() * allMoves.length)];
      } while (move === lastMove);
      
      scramble.push(move + modifiers[Math.floor(Math.random() * modifiers.length)]);
      lastMove = move;
    }
    
    return scramble.join(" ");
  }, [cubeType]);

  useEffect(() => {
    setScramble(generateScramble());
  }, [generateScramble]);

  // Manejo del timer principal
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setTime(Date.now() - startTimeRef.current);
      }, 15);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isRunning]);

  // Manejo de la inspección (cambio de rojo a verde después de 1 segundo)
  useEffect(() => {
    if (isSpacePressed && !isRunning && !isReady) {
      // Iniciar contador para luz verde
      inspectionTimerRef.current = setTimeout(() => {
        setIsGreenLight(true);
      }, 1000);
    } else {
      clearTimeout(inspectionTimerRef.current);
      setIsGreenLight(false);
    }

    return () => clearTimeout(inspectionTimerRef.current);
  }, [isSpacePressed, isRunning, isReady]);

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const centiseconds = Math.floor((ms % 1000) / 10);
    
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(centiseconds).padStart(2, "0")}`;
  };

  const handleKeyDown = useCallback((e) => {
    // Cualquier tecla detiene el timer si está corriendo
    if (isRunning) {
      e.preventDefault();
      stopTimer();
      return;
    }

    // Solo la barra espaciadora inicia el proceso
    if (e.code === "Space") {
      e.preventDefault();
      startInspection();
    }
  }, [isRunning]);

  const handleKeyUp = useCallback((e) => {
    if (e.code === "Space") {
      e.preventDefault();
      
      // Si la luz está verde, iniciar el timer
      if (!isRunning && isGreenLight) {
        startTimer();
      }
      
      endInspection();
    }
  }, [isRunning, isGreenLight]);

  // Funciones separadas para inspección
  const startInspection = useCallback(() => {
    setIsSpacePressed(true);
    spacePressStartRef.current = Date.now();
    setTime(0); // Reiniciar el crono a 0 al comenzar
  }, []);

  const endInspection = useCallback(() => {
    setIsSpacePressed(false);
    spacePressStartRef.current = 0;
  }, []);

  const startTimer = useCallback(() => {
    setIsRunning(true);
    startTimeRef.current = Date.now();
    setTime(0);
    setIsReady(false);
  }, []);

  const stopTimer = useCallback(() => {
    setIsRunning(false);
    const finalTime = Date.now() - startTimeRef.current;
    setTime(finalTime);
    setTimes(prev => [...prev, finalTime]);
    setScrambles(prev => [...prev, scramble]);
    setDnfStates(prev => [...prev, false]); // Default not DNF
    setScramble(generateScramble()); // Generar nuevo scramble automáticamente
    setIsReady(false);
    setIsSpacePressed(false);
  }, [scramble, generateScramble]);

  // Manejo de touch/click para móviles
  const handleTouchStart = useCallback((e) => {
    // Prevenir comportamiento por defecto para evitar doble firing
    e.preventDefault();
    
    if (isRunning) {
      stopTimer();
    } else {
      startInspection();
    }
  }, [isRunning, stopTimer]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    
    if (!isRunning && isGreenLight) {
      startTimer();
    }
    
    endInspection();
  }, [isRunning, isGreenLight, startTimer, endInspection]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp, isGreenLight]);

  const handleReset = () => {
    setTime(0);
    setIsRunning(false);
    setIsReady(false);
    setTimes([]);
    setScrambles([]);
    setDnfStates([]);
    setScramble(generateScramble());
  };

  const toggleDnf = (index) => {
    setDnfStates(prev => {
      const newStates = [...prev];
      newStates[index] = !newStates[index];
      return newStates;
    });
  };

  const deleteTime = (index) => {
    setTimes(prev => prev.filter((_, i) => i !== index));
    setScrambles(prev => prev.filter((_, i) => i !== index));
    setDnfStates(prev => prev.filter((_, i) => i !== index));
  };

  const handleExportResults = () => {
    if (times.length === 0) {
      alert('No hay tiempos para exportar');
      return;
    }

    // Crear contenido CSV con BOM para Excel
    const bom = '\uFEFF';
    const headers = ['N°', 'Scramble', 'Tiempo (ms)', 'Tiempo Formateado', 'DNF'];
    const csvContent = [
      headers.join(','),
      ...times.map((t, i) => 
        `${i + 1},"${scrambles[i] || 'N/A'}",${t},${formatTime(t).replace(':', '.')},${dnfStates[i] ? 'Sí' : 'No'}`
      )
    ].join('\n');

    // Agregar estadísticas al final
    const statsLines = [
      '',
      '=== ESTADÍSTICAS ===',
      `Total de tiempos: ${times.length}`,
      `Mejor tiempo: ${formatTime(stats.min)}`,
      `Peor tiempo: ${formatTime(stats.max)}`,
      `Promedio (sin mejor/peor): ${formatTime(stats.avg)}`,
      `Desviación: ${formatTime(Math.abs(stats.deviation))}`
    ];

    const finalContent = bom + csvContent + '\n' + statsLines.join('\n');

    // Crear y descargar archivo
    const blob = new Blob([finalContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `timer-cuberos-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCubeChange = (e) => {
    setCubeType(Number(e.target.value));
    handleReset();
  };

  // Cálculos de estadísticas
  const calculateStats = (timeList) => {
    // Filter out DNF times for stats calculation
    const validTimes = timeList.filter((_, idx) => !dnfStates[idx]);
    
    if (validTimes.length < 3) return { avg: 0, min: 0, max: 0, deviation: 0 };
    
    const sorted = [...validTimes].sort((a, b) => a - b);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    
    // Promedio sin mejor y peor tiempo
    const trimmedSum = sorted.slice(1, -1).reduce((sum, t) => sum + t, 0);
    const avg = trimmedSum / (sorted.length - 2);
    const deviation = ((min + max) / 2) - avg;
    
    return { avg, min, max, deviation };
  };

  const getFilteredTimes = () => {
    // Get indices of non-DNF times for proper filtering
    const validIndices = times.map((_, idx) => idx).filter(idx => !dnfStates[idx]);
    
    let filtered = [...times];
    
    if (displayMode === "5" && filtered.length > 5) {
      filtered = filtered.slice(-5);
    } else if (displayMode === "12" && filtered.length > 12) {
      filtered = filtered.slice(-12);
    }
    
    if (!isAscending) {
      filtered.reverse();
    }
    
    return filtered;
  };

  const filteredTimes = getFilteredTimes();
  const stats = calculateStats(times);
  const filteredStats = calculateStats(filteredTimes);

  return (
    <div 
      className={`timer-container ${isDarkMode ? 'dark' : ''}`}
    >
      {/* Header con Logo y controles */}
      <header className="header">
        <div className="flex items-center gap-3">
          <img src={logoGif} alt="Cuberos.cl" className="h-24 w-auto" />
          <h1 className="header-title"></h1>
        </div>
        <span className="header-subtitle">TimerCUBEROS 3.0</span>
        
        {/* Botón modo oscuro */}
        <div className="header-controls">
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`btn px-3 py-2 flex items-center gap-2 ${isDarkMode ? 'bg-yellow-500 text-gray-900 hover:bg-yellow-400' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
            title={isDarkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
          >
            {isDarkMode ? '☀️' : '🌙'} {isDarkMode ? 'Claro' : 'Oscuro'}
          </button>
        </div>
      </header>

      {/* Main Timer Card */}
      <div className="timer-card">
        {/* Cube Type Selector */}
        <div className="cube-selector">
          <label className="cube-label">Tipo de Cubo:</label>
          <select
            className="cube-select"
            value={cubeType}
            onChange={handleCubeChange}
          >
            {CUBE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Timer Display con Indicador Visual */}
        <div 
          className="timer-display"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleTouchStart}
          onMouseUp={handleTouchEnd}
          style={{ touchAction: 'manipulation' }}
        >
          {/* Indicador visual */}
          <div className={`timer-indicator ${isGreenLight ? 'green' : isSpacePressed ? 'red' : isRunning ? 'yellow' : (isDarkMode ? 'gray-dark' : 'gray')}`}>
            <div className="indicator-inner">
              <span className="text-3xl"><img src={starPng} alt="Star" /></span>
            </div>
          </div>
          
          <div className={`timer-time ${isGreenLight ? 'text-green-500' : isSpacePressed ? 'text-red-500' : isRunning ? (isDarkMode ? 'text-white' : 'text-cyan-800') : (isDarkMode ? 'text-gray-300' : 'text-cyan-600')}`}>
            {formatTime(time)}
          </div>
          
          {/* Scramble */}
          <div className={`scramble-box ${isDarkMode ? 'dark' : ''}`}>
            <p className={`scramble-text ${isDarkMode ? 'text-gray-200' : 'text-cyan-900'}`}>
              {scramble}
            </p>
          </div>
          
         
          
          {/* Visualización del Cubo */}
          {showCubePreview && (
            <div className="mt-4 w-full max-w-2xl">
              <CubeVisualization scramble={scramble} cubeSize={cubeType} />
            </div>
          )}
        </div>

        {/* Status Indicator */}
        <div className="status-indicator">
          <div className={`status-dot ${isGreenLight ? 'green' : isSpacePressed ? 'red' : isRunning ? 'yellow' : 'gray'}`}></div>
          <span className={`status-text ${isDarkMode ? 'text-gray-300' : 'text-cyan-700'}`}>
            {isGreenLight 
              ? "¡Listo! Suelta para iniciar" 
              : isSpacePressed 
                ? "Mantén presionado..." 
                : isRunning 
                  ? "Corriendo..." 
                  : "Mantén presionado para iniciar"}
          </span>
        </div>

        {/* Control Buttons */}
        <div className="control-buttons">
           {/* Botón para previsualizar cubo */}
          <button
            onClick={() => setShowCubePreview(!showCubePreview)}
            className={`btn ${showCubePreview ? (isDarkMode ? 'bg-cyan-600 text-white' : 'bg-cyan-700 text-white') : (isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')}`}
          >
            {showCubePreview ? "👁️ Ocultar Vista" : "👁️ Previsualizar Cubo"}
          </button>
          <button
            onClick={handleExportResults}
            className={`btn ${isDarkMode ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-green-700 text-white hover:bg-green-900'}`}
            disabled={times.length === 0}
          >
            <span>📊</span>
            Exportar Resultados
          </button>
          <button
            onClick={handleReset}
            className={`btn ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-cyan-700 text-white hover:bg-cyan-900'}`}
          >
            Reiniciar
          </button>
          <button
            onClick={() => setScramble(generateScramble())}
            className={`btn ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-600 text-white hover:bg-gray-800'}`}
          >
            Nuevo Scramble
          </button>
        </div>
      </div>

      {/* Statistics Section */}
      {times.length > 0 && (
        <div className={`stats-card ${isDarkMode ? 'dark' : ''}`}>
          <h2 className={`stats-title ${isDarkMode ? 'text-white' : 'text-cyan-800'}`}>Estadísticas</h2>
          
          {/* Summary Stats */}
          <div className="stats-grid">
            <div className={`stat-box ${isDarkMode ? 'dark' : 'bg-cyan-50'}`}>
              <p className={`stat-label ${isDarkMode ? 'text-gray-400' : 'text-cyan-600'}`}>Promedio</p>
              <p className={`stat-value ${isDarkMode ? 'text-white' : 'text-cyan-800'}`}>{formatTime(stats.avg)}</p>
            </div>
            <div className={`stat-box ${isDarkMode ? 'dark' : 'bg-green-50'}`}>
              <p className={`stat-label ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Mejor</p>
              <p className={`stat-value ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{formatTime(stats.min)}</p>
            </div>
            <div className={`stat-box ${isDarkMode ? 'dark' : 'bg-red-50'}`}>
              <p className={`stat-label ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Peor</p>
              <p className={`stat-value ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>{formatTime(stats.max)}</p>
            </div>
            <div className={`stat-box ${isDarkMode ? 'dark' : 'bg-purple-50'}`}>
              <p className={`stat-label ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Desviación</p>
              <p className={`stat-value ${isDarkMode ? 'text-purple-400' : 'text-purple-800'}`}>{formatTime(Math.abs(stats.deviation))}</p>
            </div>
            <div className={`stat-box ${isDarkMode ? 'dark' : 'bg-gray-50'}`}>
              <p className={`stat-label ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total</p>
              <p className={`stat-value ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{times.length}</p>
            </div>
          </div>

          {/* Individual Times */}
          <div className="times-section">
            <div className="times-header">
              <h3 className={`times-title ${isDarkMode ? 'text-white' : 'text-cyan-800'}`}>Tiempos Individuales</h3>
              
              <div className="times-filters">
                <button
                  onClick={() => setDisplayMode("5")}
                  className={`filter-btn ${displayMode === "5" ? (isDarkMode ? 'bg-cyan-600 text-white' : 'bg-cyan-700 text-white') : (isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700')}`}
                >
                  5
                </button>
                <button
                  onClick={() => setDisplayMode("12")}
                  className={`filter-btn ${displayMode === "12" ? (isDarkMode ? 'bg-cyan-600 text-white' : 'bg-cyan-700 text-white') : (isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700')}`}
                >
                  12
                </button>
                <button
                  onClick={() => setDisplayMode("all")}
                  className={`filter-btn ${displayMode === "all" ? (isDarkMode ? 'bg-cyan-600 text-white' : 'bg-cyan-700 text-white') : (isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700')}`}
                >
                  Todos
                </button>
                <button
                  onClick={() => setIsAscending(!isAscending)}
                  className={`filter-btn ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}
                >
                  {isAscending ? "↑" : "↓"}
                </button>
              </div>
            </div>

            <div className="times-list">
              <div className="space-y-2">
                {filteredTimes.map((t, i) => {
                  const originalIndex = isAscending ? i : times.length - 1 - i;
                  const isBest = t === stats.min;
                  const isWorst = t === stats.max;
                  const isDnf = dnfStates[originalIndex] || false;
                  
                  return (
                    <div
                      key={i}
                      className={`time-item ${isBest ? 'best' : isWorst ? 'worst' : (isDarkMode ? 'dark' : '')} ${isDnf ? 'dnf' : ''}`}
                    >
                      <div className="time-item-left">
                        <span className={`time-scramble ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {originalIndex + 1}. {scrambles[originalIndex]}
                        </span>
                        {isDnf && <span className="dnf-badge">DNF</span>}
                      </div>
                      <div className="time-item-right">
                        <span className={`time-value ${isDnf ? 'dnf-text' : (isBest ? (isDarkMode ? 'text-green-400' : 'text-green-700') : isWorst ? (isDarkMode ? 'text-red-400' : 'text-red-700') : (isDarkMode ? 'text-white' : 'text-cyan-800'))}`}>
                          {isDnf ? 'DNF' : formatTime(t)}
                        </span>
                        <button
                          onClick={() => toggleDnf(originalIndex)}
                          className={`dnf-btn ${isDnf ? 'active' : ''}`}
                          title={isDnf ? "Quitar DNF" : "Marcar como DNF"}
                        >
                          {isDnf ? '✓' : '○'}
                        </button>
                        <button
                          onClick={() => deleteTime(originalIndex)}
                          className="delete-btn"
                          title="Eliminar tiempo"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={`footer ${isDarkMode ? 'dark' : ''}`}>
        <p>
          <strong>Instrucciones:</strong> Mantén presionada la barra espaciadora o toca la pantalla. 
          El indicador se pondrá <span className="text-red-300 font-bold">ROJO</span> y luego <span className="text-green-300 font-bold">VERDE</span> después de 1 segundo. 
          Suelta cuando esté VERDE para iniciar. Presiona cualquier tecla o toca la pantalla para detener.
        </p>
        <p className={`footer-credit ${isDarkMode ? 'text-gray-400' : 'text-cyan-200'}`}>
          TimerCUBEROS 3.0 - Desarrollado por @JohnLi_cl
        </p>
      </footer>
    </div>
  );
}
